import { observer } from 'mobx-react';
import * as React from 'react';
import * as Autosuggest from 'react-autosuggest';
import debounce from 'javascript-debounce';
import './autoComplete.scss';

interface IAutoCompleteProps<T> {
    id: string;
    placeholder: string;
    resultLimit: number;
    initialValue: string;
    disableInput: boolean;
    managerIds?: string[];
    onChangeInput(): void;
    search(value: string, resultLimit: number, managerId?: string[]): Promise<T[]>|T[];
    onSuggestionSelected(item: T): void;
    onClearSearch(): void;
    getSelectedValue(item: T): string;
    getRenderedSuggestionText(item: T): string;
}

interface IAutoCompleteState<T> {
    value: string;
    suggestions: T[];
    focusInputOnSuggestionClick: boolean;
}

interface INewValueObject {
    newValue: string;
}

export class AutoComplete<T> extends React.Component<IAutoCompleteProps<T>, IAutoCompleteState<T>> {
    constructor(props: IAutoCompleteProps<T>) {
        super(props);

        this.state = {
            value: props.initialValue,
            suggestions: [],
            focusInputOnSuggestionClick: false
        };

        this.onSuggestionsFetchRequested = debounce ?
            debounce(this.onSuggestionsFetchRequested.bind(this), 300) :
            this.onSuggestionsFetchRequested.bind(this);
        this.onSuggestionsClearRequested = this.onSuggestionsClearRequested.bind(this);
        this.getSuggestionValue = this.getSuggestionValue.bind(this);
        this.onSuggestionsSelected = this.onSuggestionsSelected.bind(this);
        this.renderSuggestion = this.renderSuggestion.bind(this);
        this.renderInputComponent = this.renderInputComponent.bind(this);
        this.clear = this.clear.bind(this);
    }

    public render(): React.ReactNode {
        const { suggestions, value, focusInputOnSuggestionClick } = this.state;
        const inputProps = {
            id: this.props.id,
            placeholder: this.props.placeholder,
            value,
            onChange: this.onChange.bind(this),
            disabled: this.props.disableInput
        };

        return (
            <Autosuggest
                suggestions={suggestions}
                onSuggestionsFetchRequested={this.onSuggestionsFetchRequested}
                onSuggestionsClearRequested={this.onSuggestionsClearRequested}
                getSuggestionValue={this.getSuggestionValue}
                onSuggestionSelected={this.onSuggestionsSelected}
                renderSuggestion={this.renderSuggestion}
                renderInputComponent={this.renderInputComponent}
                focusInputOnSuggestionClick={focusInputOnSuggestionClick}
                highlightFirstSuggestion={true}
                inputProps={inputProps}
            />
        );
    }

    public clear(): void {
        this.setState({
            value: '',
            suggestions: []
        });

        this.props.onClearSearch();
    }

    protected async onSuggestionsFetchRequested({ value }: IValueObject): Promise<void> {
        const { resultLimit } = this.props;

        if (this.state.value !== value) {
            return;
        }

        this.setState({
            suggestions: await this.props.search(value, resultLimit, this.props.managerIds)
        });
    }

    // tslint:disable-next-line:no-any react-autosuggest is lazy so we have to suffer
    protected onChange(event: React.FormEvent<any>, { newValue }: INewValueObject): void {
        this.setState({
            value: newValue
        });

        this.props.onChangeInput();
    }

    protected onSuggestionsSelected(
        // tslint:disable-next-line:no-any react-autosuggest is lazy so we have to suffer
        event: React.FormEvent<any>,
        data: Autosuggest.SuggestionSelectedEventData<T>): void {
        this.props.onSuggestionSelected(data.suggestion);
    }

    protected onSuggestionsClearRequested = () => {
        this.setState({
            suggestions: []
        });
    }

    protected getSuggestionValue(suggestion: T): string {
        return this.props.getSelectedValue(suggestion);
    }

    protected renderSuggestion(suggestion: T): React.ReactNode {
        return (
            <span>{this.props.getRenderedSuggestionText(suggestion)}</span>
        );
    }

    // tslint:disable-next-line:no-any react-autosuggest is lazy so we have to suffer
    protected renderInputComponent(inputProps: any): React.ReactNode {
        const iconClass = inputProps.value.length > 0 ? '' : 'icon-hidden';
        return (
            <div className="autocomplete-input input-group">
                <span className="icon-search" />
                <input {...inputProps} className="pxl-form-control search-input" />
                <button
                    onClick={this.clear}
                    className={`pxl-link icon-cancel ${iconClass}`}
                />
            </div>
        );
    }
}

export default observer(AutoComplete);